<script setup>
import { useGameStore } from '@/stores/gameStore';
import { ref, onMounted } from 'vue';
import SettingsModal from '../components/SettingsModal.vue';
import { useRoute } from 'vue-router';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import DOMPurify from 'dompurify';
import linkifyHtml from 'linkify-html';
// import GameSettings from '../components/GameSettings.vue';
import GameControls from '../components/GameControls.vue';
import GameTimer from '../components/GameTimer.vue';
import CardContainer from '../components/CardContainer.vue';
import JobsDoneContainer from '../components/JobsDoneContainer.vue';
import FontSizeAdjuster from '../components/FontSizeAdjuster.vue';
import GameSummaryModal from '../components/GameSummaryModal.vue';
// import NotesPanel from '../components/NotesPanel.vue';


const { isEnabled } = useFeatureFlags();
const showExplanationModal = ref(false);
const explanationData = ref({ title: '', explanation: '' });


const openExplanationModal = (data) => {
  // data is an object with title and explanation
  explanationData.value = data;
  showExplanationModal.value = true;
  console.log("Opening explanation modal with:", data);
};

const renderExplanationHtml = (html) => {
  const linked = linkifyHtml(html || '', {
    defaultProtocol: 'https',
    target: '_blank',
    rel: 'noopener noreferrer',
    ignoreTags: ['a', 'script', 'style'],
  });

  return DOMPurify.sanitize(linked, {
    ADD_ATTR: ['target', 'rel'],
  });
};

const route = useRoute(); // Get current route
const gameStore = useGameStore();


onMounted(async () => {
  // Fetch departments for timer color
  await gameStore.fetchDepartments();
  
  // Check if there's a departmentId in the route parameters
  const departmentId = route.params.departmentId;
  if (departmentId) {
    // Fetch only cards that belong to the selected department
    const depId = Number(departmentId);
    if (Number.isFinite(depId)) {
      gameStore.setSelectedDepartment(depId);
      gameStore.fetchCardsByDepartment(depId);
    } else {
      gameStore.setSelectedDepartment(null);
      gameStore.fetchCards();
    }
  } else {
    // Fallback to the default card fetch
    gameStore.fetchCards();
  }
  
  // Load timer values from localStorage if present
  const savedTimer = localStorage.getItem('initialTimer');
  if (savedTimer) {
    gameStore.updateTimer(Number(savedTimer));
  }
  
  const savedSecondaryTimer = localStorage.getItem('secondaryTimer');
  if (savedSecondaryTimer) {
    gameStore.updateSecondaryTimer(Number(savedSecondaryTimer));
  }
});
</script>

<template>

 
  <SettingsModal 
      v-if="showExplanationModal" 
      :show="showExplanationModal" 
      @update:show="showExplanationModal = $event"
    >
      <template #header>
        <h3>{{explanationData.title }} Explanation</h3>
      </template>
      <template #body>
        <!-- Use v-html to render the explanation HTML -->
        <div v-html="renderExplanationHtml(explanationData.explanation)"></div>
        
      </template>
      <template #footer>
        <button @click="showExplanationModal = false">Close</button>
      </template>
    </SettingsModal>
    
    <!-- Game Summary Modal -->
    <GameSummaryModal v-if="isEnabled('GAME_SUMMARY')" />
    
   <!-- <transition name="slide-up">
    <GameSettings v-show="!gameStore.gameStarted" />
    </transition> -->
 
<GameTimer 
  v-if="(gameStore.gameStarted || gameStore.finishGame) && isEnabled('GAME_TIMER')" 
  class="timer-element"
  :departmentPrimaryColor="gameStore.currentDepartmentPrimaryColor"
/>

<!-- Countdown Overlay -->
<div 
  v-if="gameStore.showCountdown && isEnabled('GAME_COUNTDOWN')" 
  class="countdown-overlay"
  :style="{ '--department-primary-color': gameStore.currentDepartmentPrimaryColor }"
>
  <div class="countdown-content">
    <div class="countdown-number" :key="gameStore.countdownNumber">{{ gameStore.countdownNumber }}</div>
    <div class="countdown-text">Get Ready!</div>
  </div>
</div>

<FontSizeAdjuster v-if="isEnabled('FONT_SIZE_ADJUSTER')" />
  <div id="card-game-container">
    <header v-if="gameStore.isDemoSession" class="demo-game-intro">
      <h1 class="demo-game-title">Demo</h1><p class="demo-game-copy">The demo game limited to 8 cards.</p>
    </header>


    <div v-if="gameStore.isDemoSession" class="demo-corner-ribbon">Demo</div>
  
    <GameControls
      :gameStarted="gameStore.gameStarted"
      :canDealMoreCards="gameStore.canDealMoreCards"
      @start="gameStore.dealCards"
      @check-order="gameStore.checkOrder"
      @deal-more="gameStore.dealMoreCards"
      @reset="gameStore.resetGame"
      :isDiscussionMode="gameStore.isDiscussionMode"
    />
  
    <CardContainer
      :cards="gameStore.cards"
      :timer-running="gameStore.timerRunning"
      :selection-enabled="gameStore.selectionEnabled"
      :allow-card-reordering="gameStore.allowCardReordering"
      @update-cards="gameStore.updateCards"
      @show-explanation="openExplanationModal"
    />

    <div class="message" v-if="gameStore.gameStarted && gameStore.noMoreCards">Sorry, we are out of cards!</div>
   
    <JobsDoneContainer
      v-if="gameStore.isDiscussionMode"
      :jobs-done="gameStore.jobsDone"
      :allow-job-drop="gameStore.allowJobDrop"
      @update-jobs-done="gameStore.updateJobsDone"
      @remove-card="gameStore.removeCardFromDeck"
      :max-jobs-done-count="gameStore.maxJobsDoneCount"
      @show-explanation="openExplanationModal" 
    />
    <!-- <NotesPanel /> -->
  </div>
  
</template>

<style src="../styles/_global.scss" lang="scss"></style>

<style scoped lang="scss">
@import '@/styles/_variables';

.demo-game-intro {
  text-align: center;
  width: 100%;
  max-width: 28rem;
  margin: 0 auto 1rem;
  padding: 0 1rem;
}

.demo-game-title {
  font-family: $heading;
  font-size: var(--step-2);
  color: $brand-one-dark;
  margin: 0;
  font-weight: $bold;
}

.demo-game-copy {
  margin: 0;
  font-family: $body;
  font-size: var(--step--1);
  color: $body-color;
  line-height: 1.5;
}

header.demo-game-intro {
 position: absolute;
bottom: -1rem;
right: 0;
display: flex;
flex-direction: column;
align-items: flex-end;
 padding: 2rem;
 max-width: auto;
 width: fit-content;

}
</style>